// "Nutzung im Detail" — tabs, category mix, ranked leaderboard, and the detail
// panel for whichever row is selected. Follows the Device Usage Dashboard
// design; the shared primitives below are used by the rest of the view too.

import React, { useEffect, useState } from "react";
import { formatCount, formatDuration } from "./analyticsApi";

export function StatCard({ label, value, extra }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {extra ? <div className="stat-extra">{extra}</div> : null}
    </div>
  );
}

/** Counts per local hour. Axis labels share the bars' columns so each sits
 *  under the hour it names. */
export function Histogram({ hours }) {
  const peak = Math.max(1, ...hours);
  return (
    <div className="hist-wrap">
      <div className="hist-bars">
        {hours.map((count, hour) => (
          <div key={hour} className="hist-bar-track" title={`${String(hour).padStart(2, "0")}:00 — ${count}`}>
            <div className="hist-bar" style={{ height: count ? `${Math.max(6, (count / peak) * 100)}%` : 0 }} />
          </div>
        ))}
      </div>
      <div className="hist-axis">
        {hours.map((_, hour) => (
          <div key={hour} className="hist-axis-cell">
            {hour % 6 === 0 ? (
              <span style={hour === 0 ? { left: 0 } : { left: "50%", transform: "translateX(-50%)" }}>
                {String(hour).padStart(2, "0")}:00
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function dayLabel(date, includeYear = false) {
  const [year, month, day] = String(date || "").split("-");
  if (!year || !month || !day) return date || "—";
  return includeYear ? `${day}.${month}.${year}` : `${day}.${month}.`;
}

/** Duration per local calendar day for the selected dashboard range. */
export function DailyDurationChart({ days }) {
  const rows = days || [];
  if (!rows.length) return <div className="empty-note">Keine Tagesdaten in diesem Zeitraum.</div>;

  const peak = Math.max(1, ...rows.map((row) => row.total_ms || 0));
  const labelEvery = rows.length <= 10 ? 1 : Math.ceil(rows.length / 6);
  const minWidth = Math.max(520, rows.length * 12);

  return (
    <div className="daily-chart-scroll">
      <div className="daily-chart" style={{ minWidth }}>
        <div className="daily-peak">{formatDuration(peak)}</div>
        <div
          className="daily-bars"
          style={{ gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))` }}>
          {rows.map((row) => {
            const duration = row.total_ms || 0;
            const title = `${dayLabel(row.date, true)} — ${formatDuration(duration)}`;
            return (
              <div key={row.date} className="daily-bar-track" title={title} aria-label={title} role="img">
                {rows.length <= 10 && duration ? (
                  <span
                    className="daily-bar-value"
                    style={{ bottom: `calc(${Math.max(4, (duration / peak) * 100)}% + 4px)` }}>
                    {formatDuration(duration)}
                  </span>
                ) : null}
                <div
                  className="daily-bar"
                  style={{ height: duration ? `${Math.max(4, (duration / peak) * 100)}%` : 0 }}
                />
              </div>
            );
          })}
        </div>
        <div
          className="daily-axis"
          style={{ gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))` }}>
          {rows.map((row, index) => (
            <div key={row.date} className="daily-axis-cell">
              {index % labelEvery === 0 || index === rows.length - 1 ? <span>{dayLabel(row.date)}</span> : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ProgressList({ rows, labels, ordered }) {
  const entries = Object.entries(rows || {});
  const total = entries.reduce((sum, [, value]) => sum + value, 0);
  if (!total) return <div className="empty-note">Keine Daten in diesem Zeitraum.</div>;

  return (
    <div className="stack-8">
      {(ordered ? entries : [...entries].sort((a, b) => b[1] - a[1]))
        .map(([key, value]) => (
          <div key={key} className="prow">
            <div className="prow-label">{labels[key] || key}</div>
            <div className="prow-track">
              <div className="prow-fill" style={{ width: `${Math.round((value / total) * 100)}%` }} />
            </div>
            <div className="prow-value">{value}</div>
          </div>
        ))}
    </div>
  );
}

// Category accents. Assigned by position rather than by name so a category
// added to the backend catalogue still gets a colour.
const MIX_COLORS = [
  "#76B3FF",
  "#4A8FE0",
  "#8E7BE0",
  "#5FC2C7",
  "#9BC7F5",
  "#A9B4C2",
];

function StackedMix({ categories, metric }) {
  const total = categories.reduce((sum, row) => sum + (row[metric] || 0), 0) || 1;
  return (
    <div className="stack-10">
      <div className="mix-bar">
        {categories.map((row, index) =>
          row[metric] ? (
            <div
              key={row.category}
              style={{ width: `${(row[metric] / total) * 100}%`, background: MIX_COLORS[index % MIX_COLORS.length] }}
              title={row.category}
            />
          ) : null,
        )}
      </div>
      <div className="mix-legend">
        {categories.map((row, index) => (
          <div key={row.category} className="mix-legend-item">
            <span className="mix-dot" style={{ background: MIX_COLORS[index % MIX_COLORS.length] }} />
            <span>{row.category}</span>
            <span className="mix-legend-value">
              {metric === "total_ms" ? formatDuration(row.total_ms) : formatCount(row.count)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BreakdownPanel({ dimensions, config, startSplitLabels }) {
  const tabs = Object.keys(config).filter((key) => dimensions[key]);
  const [tab, setTab] = useState(tabs[0] || null);
  const [category, setCategory] = useState(null);
  // undefined = nothing chosen yet, fall back to the default below;
  // null = deliberately deselected, show the active category/overall total.
  const [selected, setSelected] = useState(undefined);
  const [sort, setSort] = useState("anzahl");
  const [activityChart, setActivityChart] = useState("verlauf");

  const cfg = tab ? config[tab] : null;
  const current = tab ? dimensions[tab] : null;

  const hidden = (cfg && cfg.hide) || [];
  const strip = (rows) => (rows || []).filter((row) => !hidden.includes(row.subject));

  // Activities arrive grouped by category; every other tab is a flat list.
  const categories = current && current.categories ? current.categories : null;
  const activeCategory = categories ? categories.find((row) => row.category === category) || null : null;
  const items = strip(categories
    ? (activeCategory ? activeCategory.subjects : current.subjects)
    : current && current.subjects);

  const sortKey = cfg && cfg.showDuration && sort === "zeit" ? "total_ms" : "count";
  const ranked = [...items].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));

  // A tab change clears both levels, or a selection from the previous tab
  // leaves the panel showing nothing.
  useEffect(() => {
    setCategory(null);
    setSelected(undefined);
  }, [tab]);

  // Derived rather than stored: writing a default into state from an effect
  // re-renders on every pass and can chain indefinitely. A flat list defaults
  // to its first row; the activity list defaults to its overall total.
  const fallback = categories ? null : (ranked[0] && ranked[0].subject) || null;
  const chosen = selected === undefined ? fallback : selected;
  const effective = chosen && ranked.find((row) => row.subject === chosen) ? chosen : fallback;

  if (!tab || !current) return <div className="empty-note">Noch keine Nutzungsdaten.</div>;

  const detail = ranked.find((row) => row.subject === effective)
    || activeCategory
    || (categories ? current.total : null);
  const detailHeading = detail === current.total ? "Alle Aktivitäten" : detail && (detail.subject || detail.category);
  const max = Math.max(1, ...ranked.map((row) => row[sortKey] || 0));

  return (
    <>
      <div className="tabs">
        {tabs.map((key) => (
          <button key={key} className={key === tab ? "active" : ""} onClick={() => setTab(key)}>
            {config[key].label}
          </button>
        ))}
      </div>

      {categories ? (
        <>
          <div style={{ marginBottom: 16 }}>
            <StackedMix categories={categories} metric={sortKey} />
          </div>
          <div className="chips">
            <button
              className={`chip ${category === null ? "active" : ""}`}
              onClick={() => {
                setCategory(null);
                setSelected(undefined);
              }}>
              Alle
              <span className="chip-count">{formatCount(current.total.count)}</span>
            </button>
            {categories.map((row) => (
              <button
                key={row.category}
                className={`chip ${row.category === category ? "active" : ""}`}
                onClick={() => {
                  // Re-picking the open category returns to all activities.
                  setCategory(row.category === category ? null : row.category);
                  setSelected(undefined);
                }}>
                {row.category}
                <span className="chip-count">{formatCount(row.count)}</span>
              </button>
            ))}
          </div>
        </>
      ) : null}

      {cfg.showDuration ? (
        <div className="detail-toolbar">
          <span />
          <div className="segmented">
            <span className="sort-label">Sortieren</span>
            <button className={sort === "anzahl" ? "active" : ""} onClick={() => setSort("anzahl")}>
              Anzahl
            </button>
            <button className={sort === "zeit" ? "active" : ""} onClick={() => setSort("zeit")}>
              Zeit
            </button>
          </div>
        </div>
      ) : null}

      {!ranked.length ? (
        <div className="empty-note">Keine Daten in diesem Zeitraum.</div>
      ) : (
        <div>
          {ranked.map((row, index) => (
            <div
              key={row.subject}
              className={`lb-row lb-row-click ${row.subject === effective ? "lb-row-selected" : ""}`}
              // Only the grouped tab can deselect: there the category total is
              // something to fall back to. A flat list always shows one row.
              onClick={() => setSelected(categories && row.subject === effective ? null : row.subject)}>
              <div className={`lb-rank ${index < 3 ? "lb-rank-top" : ""}`}>{index + 1}</div>
              <div className="lb-name">{row.subject}</div>
              <div className="lb-bar-track">
                <div className="lb-bar" style={{ width: `${((row[sortKey] || 0) / max) * 100}%` }} />
              </div>
              <div className="lb-value">{formatCount(row.count)}×</div>
              {cfg.showDuration ? <div className="lb-value">{formatDuration(row.total_ms)}</div> : null}
            </div>
          ))}
        </div>
      )}

      {detail ? (
        <div className="detail-panel">
          <div className="detail-heading">{detailHeading}</div>
          <div className="stat-grid stat-grid-sm">
            <StatCard label="Anzahl" value={formatCount(detail.count)} />
            {cfg.showDuration ? <StatCard label="Gesamtzeit" value={formatDuration(detail.total_ms)} /> : null}
            {cfg.showDuration ? <StatCard label="Median" value={formatDuration(detail.median_ms)} /> : null}
            {cfg.showDuration ? <StatCard label="Durchschnitt" value={formatDuration(detail.mean_ms)} /> : null}
            <StatCard label="Aktive Tage" value={formatCount(detail.active_days)} />
          </div>

          {startSplitLabels && detail.by_start ? (
            <>
              <div className="detail-hist-label">Gestartet durch</div>
              <div style={{ marginBottom: 18 }}>
                <ProgressList rows={detail.by_start} labels={startSplitLabels} />
              </div>
            </>
          ) : null}

          {Array.isArray(detail.by_day) ? (
            <>
              <div className="detail-chart-toolbar">
                <div className="detail-hist-label">
                  {activityChart === "verlauf" ? "Nutzungszeit nach Tag" : "Nutzungen nach Tageszeit"}
                </div>
                <div className="segmented segmented-small">
                  <button
                    className={activityChart === "verlauf" ? "active" : ""}
                    onClick={() => setActivityChart("verlauf")}>
                    Verlauf
                  </button>
                  <button
                    className={activityChart === "tageszeit" ? "active" : ""}
                    onClick={() => setActivityChart("tageszeit")}>
                    Tageszeit
                  </button>
                </div>
              </div>
              {activityChart === "verlauf" ? (
                <DailyDurationChart days={detail.by_day} />
              ) : (
                <Histogram hours={detail.by_hour} />
              )}
            </>
          ) : (
            <>
              <div className="detail-hist-label">Nach Tageszeit</div>
              <Histogram hours={detail.by_hour} />
            </>
          )}
        </div>
      ) : null}
    </>
  );
}
